import { observer } from 'mobx-react-lite'
import { type KeyboardEvent } from 'react'
import classNames from 'classnames'
import { Error } from 'shared/ui'
import { Warning } from 'shared/ui/Warning'
import {
  makeTextFieldAutoFocus,
  makeTextFieldHighlight,
  makeTextFieldHighlightLimit,
  TextField,
} from 'shared/ui/TextField'
import { Label } from 'shared/ui/Label'
import { InputPassword } from 'shared/ui/TextField/InputPresets/InputPassword'
import { MAX_PASSWORD_LENGTH, MIN_PASSWORD_LENGTH } from 'shared/constants/auth'
import { type ResetPasswordStore } from 'pages/auth/pages/reset/ui/ResetPassword'
import styles from './styles.module.scss'

type IResetPasswordInputsProps = {
  store: ResetPasswordStore
}

export const ResetPasswordInputs = observer(({ store }: IResetPasswordInputsProps) => {
  const {
    password,
    setPassword,
    confirmPassword,
    setConfirmPassword,
    triggerPasswordValidation,
    passwordError,
    confirmPasswordError,
    loading,
    isMaxPassword,
    isMaxConfirmPassword,
    isActivePasswordField,
    isActiveConfirmPasswordField,
    showConstantError,
    focusPasswordField,
    focusConfirmPasswordField,
    blurPasswordField,
    blurConfirmPasswordField,
  } = store

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault()
      triggerPasswordValidation()
    }
  }
  return (
    <>
      <Label
        label={{
          text: 'New password',
          className: styles.inputLabel,
        }}
      />
      <TextField
        Input={InputPassword}
        className={classNames(styles.textField, {
          [styles.error]: !!passwordError || (isMaxPassword && isActivePasswordField),
        })}
        variant='stroke'
        InputProps={{
          placeholder: `${MIN_PASSWORD_LENGTH}-${MAX_PASSWORD_LENGTH} characters`,
          onKeyDown,
        }}
        error={!!passwordError && showConstantError}
        onFocus={focusPasswordField}
        onBlur={blurPasswordField}
        disabled={loading}
        size='medium'
        onChange={setPassword}
        value={password}
        mainActions={[
          makeTextFieldAutoFocus({ withFocus: true }),
          makeTextFieldHighlightLimit({ limit: MAX_PASSWORD_LENGTH }),
          makeTextFieldHighlight({ mode: 'error', highlight: !!passwordError }),
        ]}
      />
      <Error type={'text'} error={passwordError} />
      <Warning
        text={
          isMaxPassword && isActivePasswordField
            ? `Maximum password length is ${MAX_PASSWORD_LENGTH} characters`
            : ''
        }
      />

      <Label
        label={{
          text: 'Confirm password',
          className: styles.inputLabel,
        }}
      />
      <TextField
        Input={InputPassword}
        className={classNames(styles.textField, {
          [styles.error]:
            !!confirmPasswordError || (isMaxConfirmPassword && isActiveConfirmPasswordField),
        })}
        variant='stroke'
        InputProps={{
          placeholder: 'Confirm new password',
          onKeyDown,
        }}
        error={!!confirmPasswordError && showConstantError}
        onFocus={focusConfirmPasswordField}
        onBlur={blurConfirmPasswordField}
        disabled={loading}
        size='medium'
        onChange={setConfirmPassword}
        value={confirmPassword}
        mainActions={[
          makeTextFieldHighlightLimit({ limit: MAX_PASSWORD_LENGTH }),
          makeTextFieldHighlight({ mode: 'error', highlight: !!confirmPasswordError }),
        ]}
      />
      <Error type={'text'} error={confirmPasswordError} />
      <Warning
        text={
          isMaxConfirmPassword && isActiveConfirmPasswordField
            ? `Maximum password length is ${MAX_PASSWORD_LENGTH} characters`
            : ''
        }
      />
    </>
  )
})
